import {
  type CurrencyCode,
  formatMoney,
  formatNumber,
  formatPercentage,
} from '@gonfalon/dataviz-experimental/formatters';
import { enableCurrencyFormatMoney } from '@gonfalon/dogfood-flags/dogfood-flags';

import type { DifferenceEstimateType } from '../types';

const CURRENCY_UNIT_TO_CODE: Record<string, CurrencyCode> = {
  usd: 'USD',
  USD: 'USD',
  $: 'USD',
  eur: 'EUR',
  EUR: 'EUR',
  '€': 'EUR',
  gbp: 'GBP',
  GBP: 'GBP',
  '£': 'GBP',
};

function toCurrencyCode(unit: string): CurrencyCode | undefined {
  return CURRENCY_UNIT_TO_CODE[unit];
}

/**
 * Formats a difference value based on the estimate type and metric unit.
 *
 * - Relative mode: Always formats as percentage (e.g., "5.2%")
 * - Absolute mode with binary metric (no unit): Formats as number with "pp" unit, scaled by 100x (e.g., "0.0471 pp")
 * - Absolute mode with unit (numeric, count, etc.): Formats as number with unit (e.g., "1.5 ms", "3 units")
 *
 * Use {@link resolveMetricUnit} to get the correct metricUnit value from a Metric object.
 *
 * @param value - The numeric value to format
 * @param differenceEstimateType - Whether the difference is relative or absolute
 * @param metricUnit - Resolved metric unit (e.g., "ms", "units"). If not provided, treats as binary metric.
 * @param includeUnits - Whether to include units in the formatted string. Defaults to true.
 * @returns Formatted string representation of the value
 *
 * @example
 * ```ts
 * formatDifferenceValue(0.05, 'relative', 'ms') // "5%"
 * formatDifferenceValue(0.05, 'relative', 'ms', false) // "5"
 * formatDifferenceValue(1.5, 'absolute', 'ms') // "1.5 ms"
 * formatDifferenceValue(1.5, 'absolute', 'ms', false) // "1.5"
 * formatDifferenceValue(0.000471, 'absolute') // "0.0471 pp" (binary metric, scaled by 100x)
 * formatDifferenceValue(0.000471, 'absolute', undefined, false) // "0.0471"
 * ```
 */
export function formatDifferenceValue(
  value: number,
  differenceEstimateType: DifferenceEstimateType,
  metricUnit?: string,
  includeUnits: boolean = true,
): string {
  const isRelative = differenceEstimateType === 'relative';

  // Binary metrics measure rates (0-1). Their absolute difference is in
  // percentage points, so we scale by 100 and label with "pp".
  const isBinaryMetric = !metricUnit?.trim();

  // Relative mode: always use percentage.
  // The chart platform clamps extreme values so value * 100 is always finite.
  if (isRelative) {
    const formatted = formatPercentage(value * 100, { removeTrailingZeroes: true });
    return includeUnits ? formatted : formatted.replace(/[\u00A0 ]?%/, '').trim();
  }

  if (isBinaryMetric) {
    const formattedNumber = formatNumber(value * 100, { removeTrailingZeroes: true });
    return includeUnits ? `${formattedNumber} pp` : formattedNumber;
  }

  if (enableCurrencyFormatMoney()) {
    const currencyCode = metricUnit ? toCurrencyCode(metricUnit) : undefined;
    if (currencyCode) {
      return formatMoney(value, { currencyCode });
    }
  }

  const formattedNumber = formatNumber(value, { removeTrailingZeroes: true });
  return includeUnits ? `${formattedNumber} ${metricUnit}` : formattedNumber;
}
